import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar
} from '@mui/material';
import {
  ShoppingCart,
  Warehouse,
  HourglassEmpty,
  CheckCircle,
  AccountBalanceWallet,
} from '@mui/icons-material';
import PageMeta from '../../components/PageMeta';
import { fetchOrderStatusCounts } from '../../store/slices/orderSlice';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { statusCounts } = useSelector((state) => state.order);

  // Fetch order status counts on component mount
  useEffect(() => {
    dispatch(fetchOrderStatusCounts());
  }, [dispatch]);

  const statsCards = [
    {
      title: t('stats.unpaidOrders'),
      value: statusCounts.unpaid || 0,
      change: '+0%',
      icon: <ShoppingCart />,
      color: '#FF9800',
      path: '/admin/orders/unpaid'
    },
    {
      title: t('stats.processingOrders'),
      value: statusCounts.processing || 0,
      change: '+0%',
      icon: <HourglassEmpty />,
      color: '#2196F3',
      path: '/admin/orders/processing'
    },
    {
      title: t('stats.purchasedOrders'),
      value: statusCounts.purchased || 0,
      change: '+0%',
      icon: <CheckCircle />,
      color: '#F44336',
      path: '/admin/orders/purchased'
    },
    {
      title: t('stats.warehouseOrders'),
      value: statusCounts.warehouse || 0,
      change: '+0%',
      icon: <Warehouse />,
      color: '#4CAF50',
      path: '/admin/orders/in-warehouse'
    },
    {
      title: t('stats.topupTransactions'),
      value: t('stats.viewTransactions'),
      change: '+0%',
      icon: <AccountBalanceWallet />,
      color: '#9C27B0',
      path: '/admin/transactions/topup'
    }
  ];







  return (
    <Box>
      <PageMeta
        title={t('title')}
        description={t('dashboard:meta.description', { defaultValue: 'Admin dashboard overview for Checkstall Import orders, analytics and operations.' })}
      />
      <Typography variant="h4" sx={{ px: { xs: 1, sm: 1.5, md: 2 } , mb: 3, fontWeight: 'bold', color: '#DC143C' }}>
        {t('title')}
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ px: { xs: 1, sm: 1.5, md: 2 } , mb: 4 }}>
        {statsCards.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3, xl: 3 }} key={index}>
            <Card
              role="button"
              tabIndex={0}
              onClick={() => navigate(card.path)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(card.path);
                }
              }}
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                border: '1px solid #e0e0e0',
                cursor: 'pointer',
                transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                '&:hover, &:focus-visible': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4,
                  borderColor: card.color,
                  outline: 'none'
                }
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: card.color, mb: 1 }}>
                      {card.value}
                    </Typography>
                    <Chip
                      label={card.change}
                      size="small"
                      sx={{
                        backgroundColor: card.change.startsWith('+') ? '#4CAF50' : '#F44336',
                        color: 'white',
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>
                  <Avatar sx={{ bgcolor: card.color, width: 56, height: 56 }}>
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>


    </Box>
  );
};

export default Dashboard; 
